import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  updateStock,
  getSales,
  createSale,
  cancelSale,
  getStoreStats,
  type Product,
  type Sale,
  type StoreStats,
  type PaymentMethod,
} from '../../api/store.api';
import { useAuth } from '../../context/AuthContext';
import { getSelectedSede } from '../../utils/sede';
import { formatCOP, CurrencyInput } from '../../utils/currencyFormat';

type Tab = 'vender' | 'ventas' | 'productos' | 'resumen';

const PAYMENT_METHODS: { value: PaymentMethod; label: string; icon: string }[] = [
  { value: 'EFECTIVO', label: 'Efectivo', icon: 'ri-money-dollar-box-line' },
  { value: 'TARJETA', label: 'Tarjeta', icon: 'ri-bank-card-line' },
  { value: 'TRANSFERENCIA', label: 'Transferencia', icon: 'ri-exchange-dollar-line' },
  { value: 'QR', label: 'QR', icon: 'ri-qr-code-line' },
];

const errorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Tienda() {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'ADMIN';
  // Las ventas y el inventario son de una sede: en el consolidado solo se consulta
  const hasSede = !!getSelectedSede() || user?.rol === 'OPERADOR';
  const [tab, setTab] = useState<Tab>('vender');

  const tabs: { id: Tab; label: string; icon: string; adminOnly?: boolean }[] = [
    { id: 'vender', label: 'Vender', icon: 'ri-shopping-cart-2-line' },
    { id: 'ventas', label: 'Ventas', icon: 'ri-receipt-line' },
    { id: 'productos', label: 'Productos e inventario', icon: 'ri-archive-line', adminOnly: true },
    { id: 'resumen', label: 'Resumen', icon: 'ri-bar-chart-2-line', adminOnly: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Tienda</h1>
        <p className="text-slate-500 mt-1">
          Venta de productos. Las ventas entran a la caja de tu turno y descuentan el inventario de la sede.
        </p>
      </div>

      {!hasSede && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center gap-2">
          <i className="ri-map-pin-2-line text-lg"></i>
          Estás viendo todas las sedes. Elige una sede en la barra superior para vender o ajustar inventario.
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs
          .filter((t) => !t.adminOnly || isAdmin)
          .map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                tab === t.id ? 'bg-teal-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className={`${t.icon} mr-2`}></i>
              {t.label}
            </button>
          ))}
      </div>

      {tab === 'vender' && <PuntoDeVenta disabled={!hasSede} />}
      {tab === 'ventas' && <HistorialVentas isAdmin={isAdmin} />}
      {tab === 'productos' && isAdmin && <Productos canAdjust={hasSede} />}
      {tab === 'resumen' && isAdmin && <Resumen />}
    </div>
  );
}

// ==================== PUNTO DE VENTA ====================

function PuntoDeVenta({ disabled }: { disabled: boolean }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [metodoPago, setMetodoPago] = useState<PaymentMethod>('EFECTIVO');
  const [recibido, setRecibido] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSale, setLastSale] = useState<Sale | null>(null);

  const load = async () => {
    try {
      setProducts(await getProducts());
    } catch (error) {
      toast.error(errorMessage(error, 'Error cargando productos'));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.categoria).filter(Boolean))] as string[],
    [products],
  );

  const visible = products.filter((p) => {
    const q = search.trim().toLowerCase();
    const matches = !q || p.nombre.toLowerCase().includes(q) || p.codigo?.toLowerCase().includes(q);
    return matches && (!category || p.categoria === category);
  });

  const cartLines = Object.entries(cart)
    .map(([id, cantidad]) => ({ product: products.find((p) => p.id === id)!, cantidad }))
    .filter((l) => l.product);
  const total = cartLines.reduce((sum, l) => sum + l.product.precio * l.cantidad, 0);

  const available = (p: Product) => (p.controlaStock ? p.stock : Infinity);

  const add = (p: Product, delta = 1) => {
    setCart((prev) => {
      const next = Math.max(0, Math.min((prev[p.id] ?? 0) + delta, available(p)));
      if (delta > 0 && next === (prev[p.id] ?? 0)) toast.warning(`No hay más existencias de ${p.nombre}`);
      const copy = { ...prev };
      if (next === 0) delete copy[p.id];
      else copy[p.id] = next;
      return copy;
    });
  };

  // Lector de código de barras: Enter en el buscador agrega el producto con ese código
  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const match = products.find((p) => p.codigo && p.codigo.toLowerCase() === search.trim().toLowerCase());
    if (match) {
      add(match);
      setSearch('');
    }
  };

  const handleCheckout = async () => {
    if (cartLines.length === 0) return;
    setIsSaving(true);
    try {
      const sale = await createSale({
        items: cartLines.map((l) => ({ productId: l.product.id, cantidad: l.cantidad })),
        metodoPago,
      });
      toast.success(`Venta ${sale.numero} registrada por ${formatCOP(sale.total)}`);
      setLastSale(sale);
      setCart({});
      setRecibido(0);
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'Error registrando la venta'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
              placeholder="Buscar por nombre o escanear código"
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {categories.length > 0 && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl bg-white"
            >
              <option value="">Todas las categorías</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </div>

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            <i className="ri-archive-line text-4xl mb-2 block"></i>
            No hay productos. Un administrador puede crearlos en "Productos e inventario".
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {visible.map((p) => {
              const agotado = p.controlaStock && p.stock <= 0;
              return (
                <button
                  key={p.id}
                  disabled={disabled || agotado}
                  onClick={() => add(p)}
                  className="bg-white rounded-2xl border border-slate-200 p-4 text-left hover:border-teal-400 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <p className="font-semibold text-slate-800 line-clamp-2">{p.nombre}</p>
                  {p.categoria && <p className="text-xs text-slate-400 mt-0.5">{p.categoria}</p>}
                  <p className="text-teal-600 font-bold mt-2">{formatCOP(p.precio)}</p>
                  <p className={`text-xs mt-1 ${agotado ? 'text-red-500' : 'text-slate-500'}`}>
                    {p.controlaStock ? (agotado ? 'Agotado' : `${p.stock} disponibles`) : 'Sin control de stock'}
                  </p>
                  {cart[p.id] && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium">
                      {cart[p.id]} en la venta
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 h-fit lg:sticky lg:top-20 space-y-4">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <i className="ri-shopping-cart-2-line text-teal-600"></i>
          Venta actual
        </h3>

        {cartLines.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Toca un producto para agregarlo.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {cartLines.map(({ product, cantidad }) => (
              <div key={product.id} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{product.nombre}</p>
                  <p className="text-xs text-slate-500">
                    {formatCOP(product.precio)} × {cantidad} = {formatCOP(product.precio * cantidad)}
                  </p>
                </div>
                <button onClick={() => add(product, -1)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200">
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="w-6 text-center text-sm font-semibold">{cantidad}</span>
                <button onClick={() => add(product, 1)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200">
                  <i className="ri-add-line"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {PAYMENT_METHODS.map((m) => (
            <button
              key={m.value}
              onClick={() => setMetodoPago(m.value)}
              className={`px-3 py-2 rounded-xl text-sm border transition ${
                metodoPago === m.value ? 'bg-teal-50 border-teal-400 text-teal-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className={`${m.icon} mr-1`}></i>
              {m.label}
            </button>
          ))}
        </div>

        {metodoPago === 'EFECTIVO' && cartLines.length > 0 && (
          <div className="space-y-1">
            <label className="text-sm text-slate-600">Recibido</label>
            <CurrencyInput
              value={recibido}
              onChange={setRecibido}
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {recibido > 0 && (
              <p className={`text-sm ${recibido >= total ? 'text-slate-600' : 'text-red-500'}`}>
                {recibido >= total ? `Cambio: ${formatCOP(recibido - total)}` : `Faltan ${formatCOP(total - recibido)}`}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-slate-600">Total</span>
          <span className="text-2xl font-bold text-slate-800">{formatCOP(total)}</span>
        </div>

        <button
          onClick={handleCheckout}
          disabled={disabled || isSaving || cartLines.length === 0}
          className="w-full py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Registrando...' : 'Cobrar'}
        </button>

        {lastSale && (
          <div className="text-xs text-slate-500 text-center">
            Última venta: {lastSale.numero} · {formatCOP(lastSale.total)} · {lastSale.metodoPago}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== HISTORIAL DE VENTAS ====================

function HistorialVentas({ isAdmin }: { isAdmin: boolean }) {
  const [sales, setSales] = useState<Sale[]>([]);
  const [fechaInicio, setFechaInicio] = useState(todayISO());
  const [fechaFin, setFechaFin] = useState(todayISO());
  const [isLoading, setIsLoading] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      setSales(await getSales({ fechaInicio, fechaFin }));
    } catch (error) {
      toast.error(errorMessage(error, 'Error cargando ventas'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [fechaInicio, fechaFin]);

  const handleCancel = async (sale: Sale) => {
    if (!confirm(`¿Anular la venta ${sale.numero}? Las unidades vuelven al inventario.`)) return;
    try {
      await cancelSale(sale.id);
      toast.success('Venta anulada');
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'Error anulando la venta'));
    }
  };

  const total = sales.filter((s) => s.status === 'COMPLETADA').reduce((sum, s) => sum + s.total, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
          <span className="text-slate-400">a</span>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
        </div>
        <p className="sm:ml-auto text-sm text-slate-600">
          {sales.length} venta(s) · Total <strong>{formatCOP(total)}</strong>
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Venta</th>
              <th className="text-left px-4 py-3 font-medium">Fecha</th>
              <th className="text-left px-4 py-3 font-medium">Productos</th>
              <th className="text-left px-4 py-3 font-medium">Sede</th>
              <th className="text-left px-4 py-3 font-medium">Operador</th>
              <th className="text-left px-4 py-3 font-medium">Pago</th>
              <th className="text-right px-4 py-3 font-medium">Total</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td>
              </tr>
            ) : sales.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-slate-500">No hay ventas en este periodo</td>
              </tr>
            ) : (
              sales.map((sale) => (
                <tr key={sale.id} className={`border-t border-slate-100 ${sale.status === 'ANULADA' ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs">{sale.numero}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(sale.fecha).toLocaleString('es-CO')}</td>
                  <td className="px-4 py-3">
                    {sale.items.map((i) => `${i.cantidad}× ${i.nombre}`).join(', ')}
                  </td>
                  <td className="px-4 py-3">{sale.sede?.nombre ?? '—'}</td>
                  <td className="px-4 py-3">{sale.operador?.nombre ?? '—'}</td>
                  <td className="px-4 py-3">{sale.metodoPago}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {formatCOP(sale.total)}
                    {sale.status === 'ANULADA' && <span className="block text-xs text-red-500 font-normal">Anulada</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && sale.status === 'COMPLETADA' && (
                      <button onClick={() => handleCancel(sale)} className="text-red-600 hover:underline text-xs">
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== PRODUCTOS E INVENTARIO ====================

const emptyProduct = { nombre: '', codigo: '', categoria: '', precio: 0, controlaStock: true };

function Productos({ canAdjust }: { canAdjust: boolean }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [stockProduct, setStockProduct] = useState<Product | null>(null);
  const [stockForm, setStockForm] = useState({ ajuste: 0, stockMinimo: 0, motivo: '' });
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    try {
      setProducts(await getProducts(showInactive));
    } catch (error) {
      toast.error(errorMessage(error, 'Error cargando productos'));
    }
  };

  useEffect(() => {
    load();
  }, [showInactive]);

  const openForm = (product?: Product) => {
    setEditing(product ?? null);
    setForm(
      product
        ? {
            nombre: product.nombre,
            codigo: product.codigo ?? '',
            categoria: product.categoria ?? '',
            precio: product.precio,
            controlaStock: product.controlaStock,
          }
        : emptyProduct,
    );
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editing) {
        await updateProduct(editing.id, form);
        toast.success('Producto actualizado');
      } else {
        await createProduct(form);
        toast.success('Producto creado. Registra su existencia con "Inventario".');
      }
      setShowForm(false);
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'Error guardando el producto'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      if (product.activo) {
        const result = await deleteProduct(product.id);
        toast.success(result.message);
      } else {
        await updateProduct(product.id, { activo: true });
        toast.success('Producto activado');
      }
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'Error actualizando el producto'));
    }
  };

  const openStock = (product: Product) => {
    setStockProduct(product);
    setStockForm({ ajuste: 0, stockMinimo: product.stockMinimo ?? 0, motivo: '' });
  };

  const handleStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stockProduct) return;
    setIsSaving(true);
    try {
      await updateStock(stockProduct.id, {
        ajuste: Number(stockForm.ajuste) || 0,
        stockMinimo: Number(stockForm.stockMinimo) || 0,
        motivo: stockForm.motivo || undefined,
      });
      toast.success('Inventario actualizado');
      setStockProduct(null);
      await load();
    } catch (error) {
      toast.error(errorMessage(error, 'Error actualizando inventario'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostrar inactivos
        </label>
        <p className="text-xs text-slate-500 sm:ml-4">
          El catálogo y los precios son de toda la empresa; la existencia es de la sede seleccionada.
        </p>
        <button
          onClick={() => openForm()}
          className="sm:ml-auto px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          Nuevo Producto
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Producto</th>
              <th className="text-left px-4 py-3 font-medium">Código</th>
              <th className="text-left px-4 py-3 font-medium">Categoría</th>
              <th className="text-right px-4 py-3 font-medium">Precio</th>
              <th className="text-right px-4 py-3 font-medium">Existencia</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">Aún no hay productos</td>
              </tr>
            ) : (
              products.map((p) => {
                const bajo = p.controlaStock && p.stockMinimo !== undefined && p.stock <= p.stockMinimo;
                return (
                  <tr key={p.id} className={`border-t border-slate-100 ${p.activo ? '' : 'opacity-50'}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.nombre}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.codigo || '—'}</td>
                    <td className="px-4 py-3">{p.categoria || '—'}</td>
                    <td className="px-4 py-3 text-right">{formatCOP(p.precio)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${bajo ? 'text-red-600' : 'text-slate-800'}`}>
                      {p.controlaStock ? p.stock : '—'}
                      {bajo && <span className="block text-xs font-normal">Stock bajo</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                      {p.controlaStock && (
                        <button
                          onClick={() => openStock(p)}
                          disabled={!canAdjust}
                          title={canAdjust ? 'Ajustar inventario' : 'Elige una sede para ajustar inventario'}
                          className="text-teal-600 hover:underline disabled:opacity-40 disabled:no-underline"
                        >
                          Inventario
                        </button>
                      )}
                      <button onClick={() => openForm(p)} className="text-slate-600 hover:underline">
                        Editar
                      </button>
                      <button onClick={() => handleToggleActive(p)} className={p.activo ? 'text-red-600 hover:underline' : 'text-green-600 hover:underline'}>
                        {p.activo ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleSave} className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4">
            <h2 className="text-xl font-bold text-slate-800">{editing ? 'Editar Producto' : 'Nuevo Producto'}</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
              <input
                required
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Código / barras</label>
                <input
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                <input
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  placeholder="Bebidas, Snacks..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Precio de venta *</label>
              <CurrencyInput
                required
                value={form.precio}
                onChange={(precio) => setForm({ ...form, precio })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.controlaStock}
                onChange={(e) => setForm({ ...form, controlaStock: e.target.checked })}
              />
              Controlar inventario (descontar existencias al vender)
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button type="submit" disabled={isSaving} className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium disabled:opacity-50">
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {stockProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleStock} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Inventario: {stockProduct.nombre}</h2>
              <p className="text-sm text-slate-500">Existencia actual en esta sede: {stockProduct.stock}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entrada (+) o salida (−) de unidades</label>
              <input
                type="number"
                value={stockForm.ajuste}
                onChange={(e) => setStockForm({ ...stockForm, ajuste: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Quedará en {stockProduct.stock + (Number(stockForm.ajuste) || 0)} unidades
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Motivo</label>
              <input
                value={stockForm.motivo}
                onChange={(e) => setStockForm({ ...stockForm, motivo: e.target.value })}
                placeholder="Compra a proveedor, merma, conteo..."
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stock mínimo (alerta)</label>
              <input
                type="number"
                min={0}
                value={stockForm.stockMinimo}
                onChange={(e) => setStockForm({ ...stockForm, stockMinimo: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setStockProduct(null)} className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100">
                Cancelar
              </button>
              <button type="submit" disabled={isSaving} className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium disabled:opacity-50">
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ==================== RESUMEN ====================

function Resumen() {
  const [stats, setStats] = useState<StoreStats | null>(null);
  const now = new Date();
  const [fechaInicio, setFechaInicio] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState(todayISO());

  useEffect(() => {
    getStoreStats({ fechaInicio, fechaFin })
      .then(setStats)
      .catch((error) => toast.error(errorMessage(error, 'Error cargando el resumen')));
  }, [fechaInicio, fechaFin]);

  if (!stats) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
        <span className="text-slate-400">a</span>
        <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Total vendido</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{formatCOP(stats.totalVentas)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Ventas</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{stats.cantidadVentas}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Ticket promedio</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">
            {formatCOP(stats.cantidadVentas ? stats.totalVentas / stats.cantidadVentas : 0)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Productos más vendidos</h3>
          {stats.topProductos.length === 0 ? (
            <p className="text-sm text-slate-500">Sin ventas en el periodo</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {stats.topProductos.map((p) => (
                <li key={p.productId} className="flex justify-between gap-2">
                  <span className="truncate">{p.nombre} <span className="text-slate-400">({p.cantidad})</span></span>
                  <span className="font-medium">{formatCOP(p.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Por método de pago</h3>
          <ul className="space-y-2 text-sm">
            {stats.porMetodo.map((m) => (
              <li key={m.metodoPago} className="flex justify-between">
                <span>{m.metodoPago}</span>
                <span className="font-medium">{formatCOP(m.total)}</span>
              </li>
            ))}
          </ul>
          {stats.porSede.length > 1 && (
            <>
              <h3 className="font-semibold text-slate-800 mt-5 mb-3">Por sede</h3>
              <ul className="space-y-2 text-sm">
                {stats.porSede.map((s) => (
                  <li key={s.sedeId ?? 'none'} className="flex justify-between">
                    <span>{s.sede} <span className="text-slate-400">({s.cantidad})</span></span>
                    <span className="font-medium">{formatCOP(s.total)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {stats.stockBajo.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
            <i className="ri-alert-line"></i>
            Productos con stock bajo
          </h3>
          <ul className="space-y-1 text-sm text-red-700">
            {stats.stockBajo.map((s, i) => (
              <li key={i}>
                {s.producto}
                {s.sede ? ` · ${s.sede}` : ''}: {s.cantidad} (mínimo {s.stockMinimo})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
